/*
 * cards.jsx
 *
 * Cards rendered inside DetailPanel. Each card receives a charger object (or slices of it)
 * via props and renders one themed section of the detail view.
 * Exports: CapacityCard, WorkOrderCard, SilentRiskCard, DiagnosisCard, ProactiveMaintCard,
 *          ModelConsensus, FlappingCard, RevenueCard, AlertRationale, ThroughputImpactCard.
 * Imported by: DetailPanel.jsx.
 */

import { useState } from "react";
import {
  getDiagnosis, getComponents,
  DERATING_STYLE, FLAP_STYLE,
  WO_CAT_STYLE, woStatusColor, fmtWoDate,
  timeAgo,
} from "./utils";

// ── CapacityCard ──────────────────────────────────────────────────────────────

export const CapacityCard = ({ charger }) => {
  if (!charger.derating_alert) return null;
  const sev = charger.derating_severity || "MODERATE";
  const { bg, border, color, cls } = DERATING_STYLE[sev] || DERATING_STYLE.MODERATE;
  const pct = charger.offered_vs_healthy != null ? Math.round(charger.offered_vs_healthy * 100) : null;
  return (
    <div className={`dp-card ${cls}`} style={{ background: bg, border: `1px solid ${border}`, borderLeft: `3px solid ${border}`, borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>⚡ CAPACITY ALERT — {sev} DERATING</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827", marginBottom: 6 }}>
        Delivering {pct != null ? `${pct}%` : "reduced"} of rated capacity
      </div>
      {charger.avg_offered_a > 0 && (
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 8 }}>
          Current output: <strong>{charger.avg_offered_a}A</strong>
        </div>
      )}
      <div style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.5 }}>
        This charger is accepting vehicle connections but delivering significantly less power than rated. Customers may experience slow charging.
      </div>
      {charger.rpm_alert && (
        <div style={{ fontSize: 11, color: "#7C3AED", marginTop: 8, fontWeight: 500 }}>
          🌀 Cooling fan degradation detected — avg RPM {charger.avg_rpm} (below healthy baseline). Risk of thermal derating.
        </div>
      )}
      {charger.is_nevi_compliant === false && (
        <div style={{ fontSize: 11, color: "#6B7280", marginTop: 8, borderTop: "1px solid rgba(0,0,0,0.06)", paddingTop: 8 }}>
          ⚠ NEVI uptime non-compliant — combined with capacity derating this charger may be ineligible for federal funding.
        </div>
      )}
    </div>
  );
};

// ── WorkOrderCard ─────────────────────────────────────────────────────────────

export const WorkOrderCard = ({ charger, woData }) => {
  const [showAll, setShowAll] = useState(false);

  const quickTotal  = charger?.wo_total ?? 0;
  const quickOpen   = charger?.wo_open  ?? 0;
  const quickCat    = charger?.last_wo_category;
  const quickTitle  = charger?.last_wo_title;
  const quickDate   = charger?.last_wo_date;
  const quickStatus = charger?.last_wo_status;

  if (!woData && quickTotal > 0) {
    const sc = woStatusColor(quickStatus);
    return (
      <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderLeft: "3px solid #374151", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>📋 WORK ORDER HISTORY</div>
        <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 8, lineHeight: 1.6 }}>
          <strong style={{ color: "#111827" }}>{quickTotal}</strong> total
          {quickOpen > 0 && <span style={{ color: "#D97706" }}> · <strong>{quickOpen}</strong> open</span>}
          {quickCat && <span> · Most recent: <em>{quickCat}</em></span>}
        </div>
        {quickTitle && (
          <div style={{ fontSize: 11, color: "#9CA3AF", display: "flex", gap: 6, alignItems: "center" }}>
            <span style={{ color: sc }}>{quickStatus?.toLowerCase().includes("complet") ? "✓" : quickStatus?.toLowerCase().includes("cancel") ? "—" : "●"}</span>
            <span>{quickTitle}</span>
            {quickDate && <span style={{ marginLeft: "auto" }}>{fmtWoDate(quickDate)}</span>}
          </div>
        )}
        <div style={{ fontSize: 10, color: "#9CA3AF", marginTop: 8, fontStyle: "italic" }}>Loading full history…</div>
      </div>
    );
  }

  if (!woData) return null;
  if (woData.total_wo_count === 0) return (
    <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderLeft: "3px solid #9CA3AF", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>📋 WORK ORDER HISTORY</div>
      <div style={{ fontSize: 12, color: "#9CA3AF" }}>No work order history</div>
    </div>
  );
  const wos = showAll ? woData.work_orders : woData.work_orders.slice(0, 3);
  return (
    <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderLeft: "3px solid #374151", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#374151", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>📋 WORK ORDER HISTORY</div>
      <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10, lineHeight: 1.6 }}>
        <strong style={{ color: "#111827" }}>{woData.total_wo_count}</strong> total
        {woData.open_wo_count > 0 && <span style={{ color: "#D97706" }}> · <strong>{woData.open_wo_count}</strong> open</span>}
        {woData.wo_count_90d > 0 && <span> · <strong>{woData.wo_count_90d}</strong> in last 90d</span>}
        {woData.most_common_category && <span> · Most common: <em>{woData.most_common_category}</em></span>}
        {woData.avg_days_to_resolve != null && <span> · Avg {woData.avg_days_to_resolve}d to resolve</span>}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {wos.map((wo, i) => {
          const sc     = woStatusColor(wo.latest_status);
          const catSt  = WO_CAT_STYLE[wo.category] || { bg: "#F3F4F6", color: "#374151" };
          const isOpen = sc === "#D97706";
          const incFmt  = fmtWoDate(wo.incident_report_date);
          const compFmt = fmtWoDate(wo.completed_date);
          return (
            <div key={wo.work_order_id ?? i} style={{ borderTop: i > 0 ? "1px solid #F3F4F6" : "none", paddingTop: i > 0 ? 8 : 0 }}>
              <div style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
                <span style={{ fontSize: 12, color: sc, flexShrink: 0, marginTop: 1 }}>{isOpen ? "●" : sc === "#9CA3AF" ? "—" : "✓"}</span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap", marginBottom: 2 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: "#111827" }}>{wo.latest_status?.toUpperCase().slice(0,4) || "OPEN"}</span>
                    <span style={{ fontSize: 11, fontWeight: 500, color: "#374151" }}>{wo.title || "Work Order"}</span>
                    {wo.category && (
                      <span style={{ fontSize: 9, padding: "1px 5px", borderRadius: 3, background: catSt.bg, color: catSt.color, fontWeight: 700 }}>{wo.category}</span>
                    )}
                    <span style={{ fontSize: 10, color: "#9CA3AF", marginLeft: "auto" }}>
                      {isOpen ? `filed ${incFmt}` : (incFmt && compFmt ? `${incFmt} → ${compFmt}` : incFmt)}
                    </span>
                  </div>
                  <div style={{ fontSize: 11, color: "#6B7280" }}>
                    {isOpen ? `${wo.days_to_resolve ?? 0} days open` : wo.days_to_resolve === 0 ? "same day" : `${wo.days_to_resolve} days`}
                    {wo.description && <span style={{ color: "#9CA3AF" }}> · {wo.description}</span>}
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      {woData.total_wo_count > 3 && !showAll && (
        <button onClick={() => setShowAll(true)} style={{ marginTop: 10, fontSize: 11, color: "#1E3A8A", background: "none", border: "none", cursor: "pointer", fontWeight: 600, padding: 0 }}>
          Show all {woData.total_wo_count} →
        </button>
      )}
    </div>
  );
};

// ── SilentRiskCard ────────────────────────────────────────────────────────────

export const SilentRiskCard = ({ charger, woData }) => {
  const score = charger.silent_risk_score ?? 0;
  const prob  = charger.fault_probability ?? 1;
  if (score < 0.20 || prob >= 0.40) return null;
  const hasOpenWO = (woData?.open_wo_count ?? 0) > 0;
  const recentWO  = woData?.work_orders?.[0];
  const wo90d     = charger.wo_count_90d ?? woData?.wo_count_90d ?? 0;
  const daysSince = charger.days_since_last_wo ?? woData?.days_since_last_wo ?? 999;
  const action = hasOpenWO
    ? "Open work order in queue — verify derating and capacity loss are documented"
    : "Schedule proactive inspection before fault escalation";
  return (
    <div className="dp-card dp-card-purple" style={{ background: "#F5F3FF", border: "1px solid #7C3AED", borderLeft: "3px solid #7C3AED", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#6D28D9", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>⚠ PREDICTIVE MAINTENANCE FLAG</div>
      <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.6, marginBottom: 10 }}>
        Degradation signals detected across multiple risk dimensions:
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 5, marginBottom: 10 }}>
        {charger.derating_alert && charger.offered_vs_healthy != null && (
          <div style={{ fontSize: 12, color: "#374151" }}>⚡ Delivering <strong>{Math.round(charger.offered_vs_healthy * 100)}%</strong> of rated capacity</div>
        )}
        {wo90d > 0 && (
          <div style={{ fontSize: 12, color: "#374151" }}>
            📋 <strong>{wo90d}</strong> work order{wo90d !== 1 ? "s" : ""} in last 90 days
            {recentWO && <span style={{ color: "#6B7280" }}> · {recentWO.title}{recentWO.category ? ` (${recentWO.category})` : ""}</span>}
          </div>
        )}
        {hasOpenWO && (
          <div style={{ fontSize: 12, color: "#D97706", fontWeight: 500 }}>
            ⚠ {woData.open_wo_count} open work order{woData.open_wo_count !== 1 ? "s" : ""} — maintenance in progress
          </div>
        )}
        {daysSince < 999 && !wo90d && (
          <div style={{ fontSize: 12, color: "#374151" }}>📅 Last work order: <strong>{daysSince} days ago</strong></div>
        )}
        {charger.uptime_30d != null && charger.uptime_30d < 97 && (
          <div style={{ fontSize: 12, color: "#374151" }}>⏱ Uptime: <strong>{charger.uptime_30d.toFixed(1)}%</strong> (below NEVI threshold)</div>
        )}
      </div>
      <div style={{ fontSize: 11, color: "#6D28D9", fontWeight: 600, marginBottom: 4 }}>Degradation Score: {score.toFixed(3)}</div>
      <div style={{ fontSize: 11, color: "#6B7280" }}>{action}</div>
    </div>
  );
};

// ── DiagnosisCard ─────────────────────────────────────────────────────────────

export const DiagnosisCard = ({ charger }) => {
  const d = getDiagnosis(charger);
  if (!d) return null;
  return (
    <div className="dp-card dp-card-amber" style={{ background: "#FFFBEB", border: "1px solid #F59E0B", borderLeft: "3px solid #F59E0B", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#B45309", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>🔧 MOST LIKELY CAUSE</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A", marginBottom: d.breachContext ? 6 : 10 }}>{d.cause}</div>
      {d.breachContext && <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 10 }}>{d.breachContext}</div>}
      <div style={{ fontSize: 10, color: "#F97316", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 4 }}>RECOMMENDED ACTION</div>
      <div style={{ fontSize: 11, color: "#374151", lineHeight: 1.6 }}>{d.action}</div>
    </div>
  );
};

// ── ProactiveMaintCard ────────────────────────────────────────────────────────

export const ProactiveMaintCard = ({ charger }) => {
  const parts = charger.bilstm_parts_prob;
  const fault = charger.bilstm_fault_prob;
  if (parts == null || parts < 0.30) return null;
  const pct        = Math.round(parts * 100);
  const actionDays = parts >= 0.70 ? 3 : parts >= 0.50 ? 7 : 14;
  const components = getComponents(charger);
  const scoredAgo  = charger.bilstm_scored_at ? timeAgo(charger.bilstm_scored_at) : "—";

  const signals = [];
  if (charger.derating_alert && charger.offered_vs_healthy != null)
    signals.push(`Delivering ${Math.round(charger.offered_vs_healthy * 100)}% of rated capacity`);
  if (charger.deviation?.breaches?.some(b => b.feature.includes('fault_rate')))
    signals.push("Hardware fault rate trending above personal baseline");
  if (charger.deviation?.breaches?.some(b => b.feature.includes('session')))
    signals.push("Session success rate declining");
  if (charger.deviation?.breaches?.some(b => b.feature.includes('recovery')))
    signals.push("Recovery rate degrading");
  if (charger.error_info?.impact === 'H')
    signals.push(`Recent hardware fault: ${(charger.error_info.description || "").slice(0, 60)}`);

  return (
    <div className="dp-card dp-card-indigo" style={{ background: "#EEF2FF", border: "1px solid #4F46E5", borderLeft: "3px solid #4F46E5", borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#4F46E5", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>🔧 PROACTIVE MAINTENANCE PREDICTION</div>
      <div style={{ fontSize: 13, fontWeight: 700, color: "#1E3A8A", marginBottom: 8 }}>
        {pct}% probability of parts replacement in the next 14 days
      </div>
      {signals.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 11, color: "#374151", fontWeight: 600, marginBottom: 4 }}>Based on degradation patterns over the last 7 days:</div>
          {signals.map((s, i) => <div key={i} style={{ fontSize: 11, color: "#374151" }}>▼ {s}</div>)}
        </div>
      )}
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 11, color: "#374151", fontWeight: 600, marginBottom: 4 }}>Likely components to prepare:</div>
        {components.map((c, i) => <div key={i} style={{ fontSize: 11, color: "#374151" }}>• {c}</div>)}
      </div>
      <div style={{ fontSize: 11, color: "#4F46E5", fontWeight: 600, marginBottom: 6 }}>
        Recommended action: Schedule inspection within {actionDays} days
      </div>
      <div style={{ fontSize: 10, color: "#6B7280", borderTop: "1px solid #C7D2FE", paddingTop: 6 }}>
        BiLSTM v2 · 14-day forecast · AUC 0.85
        {fault != null && <span> · Fault head: {Math.round(fault * 100)}%</span>}
        {" · "}Last scored: {scoredAgo}
      </div>
    </div>
  );
};

// ── ModelConsensus ────────────────────────────────────────────────────────────

export const ModelConsensus = ({ charger }) => {
  const xgbFlag     = (charger.fault_probability ?? 0) >= 0.60;
  const bilstmFault = (charger.bilstm_fault_prob  ?? 0) >= 0.50;
  const bilstmParts = (charger.bilstm_parts_prob  ?? 0) >= 0.50;
  const silentFlag  = (charger.silent_risk_score  ?? 0) >= 0.40;
  const flagCount   = [xgbFlag, bilstmFault, bilstmParts, silentFlag].filter(Boolean).length;
  if (flagCount === 0) return null;
  const confLabel = flagCount >= 3 ? "HIGH CONFIDENCE" : flagCount === 2 ? "MODERATE CONFIDENCE" : "LOW CONFIDENCE";
  const confColor = flagCount >= 3 ? "#EF4444" : flagCount === 2 ? "#F97316" : "#F59E0B";
  const models = [
    { label: "XGBoost v6 (real-time fault)",  flagged: xgbFlag,     prob: charger.fault_probability },
    { label: "BiLSTM fault head (sequence)",  flagged: bilstmFault, prob: charger.bilstm_fault_prob },
    { label: "BiLSTM parts head (14-day)",    flagged: bilstmParts, prob: charger.bilstm_parts_prob },
    { label: "Silent Risk (rule-based)",      flagged: silentFlag,  prob: charger.silent_risk_score  },
  ];
  return (
    <div className="dp-card" style={{ background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#6B7280", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>MODEL CONSENSUS</div>
      <div style={{ display: "flex", flexDirection: "column", gap: 4, marginBottom: 8 }}>
        {models.map(({ label, flagged, prob }) => (
          <div key={label} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 11 }}>
            <span style={{ color: flagged ? "#16A34A" : "#D1D5DB", fontWeight: 700, flexShrink: 0 }}>{flagged ? "✓" : "✗"}</span>
            <span style={{ color: flagged ? "#374151" : "#9CA3AF" }}>{label}</span>
            {prob != null && <span style={{ marginLeft: "auto", color: "#6B7280", fontFamily: "'DM Mono', monospace" }}>{Math.round(prob * 100)}%</span>}
          </div>
        ))}
      </div>
      <div style={{ fontSize: 11, color: confColor, fontWeight: 700 }}>Consensus: {flagCount}/4 — {confLabel}</div>
    </div>
  );
};

// ── FlappingCard ──────────────────────────────────────────────────────────────

export const FlappingCard = ({ charger }) => {
  if (!charger.is_flapping) return null;
  const sev = (charger.flap_severity || "active").toLowerCase();
  const s   = FLAP_STYLE[sev] || FLAP_STYLE.active;
  return (
    <div style={{ background: s.bg, border: `1px solid ${s.border}`, borderLeft: `3px solid ${s.border}`, borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: s.color, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8 }}>
        ⚡ FLAPPING — {(charger.flap_severity || "ACTIVE").toUpperCase()}
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 20 }}>
        {charger.flap_count_24hr != null && (
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{charger.flap_count_24hr}</div>
            <div style={{ fontSize: 10, color: s.color, opacity: 0.7, marginTop: 2 }}>cycles in 24h</div>
          </div>
        )}
        {charger.flap_avg_dwell_sec != null && charger.flap_avg_dwell_sec > 0 && (
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: s.color, lineHeight: 1 }}>{Math.round(charger.flap_avg_dwell_sec)}</div>
            <div style={{ fontSize: 10, color: s.color, opacity: 0.7, marginTop: 2 }}>avg sec before re-fault</div>
          </div>
        )}
      </div>
      {charger.flap_dominant_error && (
        <div style={{ fontSize: 11, color: s.color, opacity: 0.8, marginTop: 8 }}>
          Driving error: <strong>{charger.flap_dominant_error}</strong>
        </div>
      )}
    </div>
  );
};

// ── RevenueCard ───────────────────────────────────────────────────────────────

export const RevenueCard = ({ data30, data7 }) => {
  const rev      = data30?.revenue_usd || 0;
  const sessions = data30?.sessions    || 0;
  const kwh      = data30?.kwh_delivered || 0;
  const kwhDay   = data30?.avg_kwh_per_day || 0;
  const avg      = data30?.avg_session_revenue || 0;
  const accent   = sessions === 0 ? "#9CA3AF" : kwhDay > 200 ? "#16A34A" : kwhDay > 80 ? "#0EA5E9" : "#D97706";
  return (
    <div className="dp-card" style={{ background: "#F9FAFB", border: `1px solid ${accent}40`, borderLeft: `3px solid ${accent}`, borderRadius: 6, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: accent, fontWeight: 700, letterSpacing: "0.08em", marginBottom: 6 }}>USAGE — LAST 30 DAYS</div>
      {sessions === 0 ? (
        <div style={{ fontSize: 13, color: "#9CA3AF" }}>No sessions recorded</div>
      ) : (
        <>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6, marginBottom: 4 }}>
            <div style={{ fontSize: 26, fontWeight: 700, color: accent, lineHeight: 1 }}>
              {kwhDay > 0 ? kwhDay : kwh.toLocaleString("en-US", { maximumFractionDigits: 0 })}
            </div>
            <div style={{ fontSize: 13, fontWeight: 600, color: accent }}>
              {kwhDay > 0 ? "kWh/day" : "kWh total"}
            </div>
          </div>
          <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 3 }}>
            <span style={{ color: "#374151", fontWeight: 600 }}>{kwh.toLocaleString("en-US", { maximumFractionDigits: 0 })} kWh</span>
            <span style={{ color: "#9CA3AF" }}> total · {sessions} sessions · {data30?.avg_kwh_per_session > 0 ? `${data30.avg_kwh_per_session} kWh/session` : ""}</span>
          </div>
          <div style={{ fontSize: 11, color: "#9CA3AF", marginBottom: 3 }}>
            Revenue: <span style={{ color: "#374151", fontWeight: 600 }}>${rev.toLocaleString("en-US", { maximumFractionDigits: 0 })}</span>
            <span style={{ color: "#9CA3AF" }}> · ${avg.toFixed(2)}/session</span>
          </div>
          {data7?.sessions > 0 && (
            <div style={{ fontSize: 10, color: "#9CA3AF" }}>
              This week: <span style={{ color: "#374151", fontWeight: 600 }}>{data7.avg_kwh_per_day > 0 ? `${data7.avg_kwh_per_day} kWh/day` : `${data7.kwh_delivered || 0} kWh`}</span>
              {" · "}{data7.sessions} sessions
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ── AlertRationale ────────────────────────────────────────────────────────────

export const AlertRationale = ({ charger, usageData }) => {
  const tier = charger.priority_tier || '';
  if (!['P1', 'P2', 'P3'].some(p => tier.startsWith(p))) return null;

  const prob = charger.fault_probability ?? 0;

  let sr = charger.session_success_rate ?? 1;
  if (usageData?.daily?.length > 0) {
    const attempted  = usageData.daily.reduce((s, d) => s + (d.attempted  || 0), 0);
    const successful = usageData.daily.reduce((s, d) => s + (d.successful || 0), 0);
    if (attempted > 0) sr = successful / attempted;
  }

  const signals = [];
  if (charger.alert_type === 'CONFIRMED_FAULT')
    signals.push(`XGBoost v6: ${Math.round(prob * 100)}% fault probability (confirmed fault threshold exceeded)`);
  if (charger.derating_alert && charger.offered_vs_healthy != null)
    signals.push(`Capacity derating — delivering ${Math.round(charger.offered_vs_healthy * 100)}% of rated power (${(charger.derating_severity || '').toLowerCase()})`);
  if (charger.alert_type === 'EARLY_WARNING')
    signals.push(`XGBoost v6: ${Math.round(prob * 100)}% fault probability (early warning zone — 40–70%)`);
  if (charger.early_warning && charger.early_warning_note)
    signals.push(charger.early_warning_note);
  if (charger.alert_type === 'SILENT_RISK')
    signals.push(`Composite degradation score: ${charger.silent_risk_score?.toFixed(2)} (derating + work order history)`);
  if (charger.bilstm_fault_prob != null && charger.bilstm_fault_prob >= 0.40)
    signals.push(`BiLSTM sequence model (fault head): ${Math.round(charger.bilstm_fault_prob * 100)}% fault probability`);
  if (charger.bilstm_parts_prob != null && charger.bilstm_parts_prob >= 0.30)
    signals.push(`BiLSTM sequence model (parts head): ${Math.round(charger.bilstm_parts_prob * 100)}% probability of parts replacement in 14 days`);
  if (charger.uptime_30d != null && charger.uptime_30d < 97)
    signals.push(`30-day uptime: ${charger.uptime_30d.toFixed(1)}% (NEVI requires ≥97%)`);

  if (signals.length === 0) return null;

  let interp;
  if (charger.alert_type === 'CONFIRMED_FAULT' && sr < 0.20)
    interp = "The fault model has high confidence this charger will fault soon — session data confirms it is already failing.";
  else if (charger.alert_type === 'CONFIRMED_FAULT')
    interp = "The fault model has high confidence this charger will fault soon.";
  else if (charger.derating_alert && sr > 0.80)
    interp = "This charger is operational but delivering significantly less power than rated — customers experience slow charging without visible errors.";
  else if (charger.derating_alert)
    interp = "This charger has severe capacity loss and sessions are failing — it requires immediate inspection.";
  else if (charger.alert_type === 'EARLY_WARNING')
    interp = "Fault signals are entering the warning range — the model is watching this charger closely.";
  else if (charger.alert_type === 'PROACTIVE_MAINTENANCE')
    interp = "No active fault detected, but wear patterns predict parts replacement within 14 days.";
  else if (charger.uptime_30d != null && charger.uptime_30d < 97)
    interp = "Charger uptime is below the NEVI 97% threshold required for federal funding compliance.";
  else
    interp = "Degradation signals are compounding across multiple dimensions without an outright fault yet.";

  const mitigating = [];
  if (sr > 0.80)
    mitigating.push(`Sessions succeeding at ${Math.round(sr * 100)}% — customers are charging but at reduced power`);
  else if (sr <= 0.20 && usageData?.daily?.length > 0)
    mitigating.push(`Session success rate: ${Math.round(sr * 100)}% — most attempted charges are failing`);
  if (charger.alert_type === 'EARLY_WARNING' && prob < 0.60)
    mitigating.push(`Fault probability (${Math.round(prob * 100)}%) is below confirmed-fault threshold — schedule, don't rush`);
  if (charger.note && !mitigating.some(m => m.includes('Session')))
    mitigating.push(charger.note);

  return (
    <div className="dp-card dp-card-slate" style={{ background: '#F8FAFC', border: '1px solid #E2E8F0', borderLeft: '3px solid #94A3B8', borderRadius: 6, padding: '10px 14px', marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: '#64748B', fontWeight: 700, letterSpacing: '0.08em', marginBottom: 6 }}>ℹ WHY THIS IS FLAGGED</div>
      <div style={{ fontSize: 12, color: '#374151', marginBottom: 8, lineHeight: 1.5 }}>{interp}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {signals.map((s, i) => (
          <div key={i} style={{ fontSize: 11, color: '#475569' }}>▸ {s}</div>
        ))}
      </div>
      {mitigating.length > 0 && (
        <div style={{ marginTop: 8, paddingTop: 7, borderTop: '1px solid #E2E8F0' }}>
          {mitigating.map((m, i) => (
            <div key={i} style={{ fontSize: 11, color: '#64748B' }}>✓ {m}</div>
          ))}
        </div>
      )}
    </div>
  );
};

// ── ThroughputImpactCard ──────────────────────────────────────────────────────

const KWH_BAND_COLOR = { P0: "#FF3B3B", P1: "#FF8C00", P2: "#FFD700", P3: "#00C896", P4: "#6B7280", P5: "#6B7280" };

export const ThroughputImpactCard = ({ charger }) => {
  const kwh  = charger.avg_kwh_day_30d;
  const band = charger.kwh_priority;
  const epw  = charger.breakdown?.effective_power_weight;
  if (kwh == null) return null;
  const bc = KWH_BAND_COLOR[band] || "#6B7280";
  return (
    <div className="dp-card" style={{ background: "#fff", border: "1px solid #e5e7eb", borderRadius: 6, padding: "10px 14px", marginBottom: 14 }}>
      <div style={{ fontSize: 10, color: "#6b7280", fontWeight: 700, letterSpacing: "0.08em", marginBottom: 8, fontFamily: "'DM Mono', monospace" }}>THROUGHPUT IMPACT</div>
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 700, color: "#111", lineHeight: 1, fontFamily: "'DM Mono', monospace" }}>{Math.round(kwh)}</div>
          <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2, fontFamily: "'DM Mono', monospace" }}>kWh / day (30d avg)</div>
        </div>
        {band && (
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: bc, padding: "2px 7px", borderRadius: 4, border: `1px solid ${bc}55`, display: "inline-block", fontFamily: "'DM Mono', monospace" }}>{band}</div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2, fontFamily: "'DM Mono', monospace" }}>throughput band</div>
          </div>
        )}
        {epw != null && (
          <div>
            <div style={{ fontSize: 14, fontWeight: 700, color: "#111", fontFamily: "'DM Mono', monospace" }}>{epw.toFixed(3)}</div>
            <div style={{ fontSize: 10, color: "#6b7280", marginTop: 2, fontFamily: "'DM Mono', monospace" }}>power weight</div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 8, fontFamily: "'DM Mono', monospace" }}>
        Higher throughput → greater revenue loss when offline
      </div>
    </div>
  );
};
